import { withPrefix } from 'gatsby'
import React, { FunctionComponent } from 'react'

import {
  IconColor,
  IconName,
  IconSize,
  IconSizeToken,
  IconSourceSize,
  IconStyle,
  PuxIconProps,
} from './icon.types'
import { iconSizeMasks } from './iconManifest'
import { getIconSpriteBucket, iconSpriteFiles } from './iconSpriteManifest'

const ICON_SIZE_MAP: Record<string, string> = {
  tiny: 'var(--Spacing-Icon-Size-Tiny)',
  small: 'var(--Spacing-Icon-Size-Small)',
  medium: 'var(--Spacing-Icon-Size-Medium)',
  large: 'var(--Spacing-Icon-Size-Large)',
}

const ICON_COLOR_MAP: Record<string, string> = {
  default: 'var(--color-text-primary)',
  muted: 'var(--color-text-secondary)',
  accent: 'var(--color-link-default)',
  inverse: 'var(--color-text-inverse)',
  success: 'var(--color-status-success)',
  error: 'var(--color-status-danger)',
  current: 'currentColor',
}

const ICON_TOKEN_SOURCE_SIZE_MAP: Record<IconSizeToken, IconSourceSize> = {
  tiny: 16,
  small: 16,
  medium: 24,
  large: 24,
}

const ICON_SOURCE_SIZES: IconSourceSize[] = [12, 16, 24, 32, 48, 64]

export const resolveIconSize = (size: IconSize = 'medium'): string => {
  if (typeof size === 'number') {
    return `${size}px`
  }

  return ICON_SIZE_MAP[size] ?? ICON_SIZE_MAP.medium
}

export const resolveIconColor = (color: IconColor = 'current'): string =>
  ICON_COLOR_MAP[color] ?? color

const isKnownIconName = (name: string): name is IconName =>
  Object.prototype.hasOwnProperty.call(iconSizeMasks, name)

const getRequestedSourceSize = (size: IconSize): IconSourceSize =>
  typeof size === 'number' ? size : ICON_TOKEN_SOURCE_SIZE_MAP[size]

export const resolveIconSourceSize = (
  name: IconName,
  size: IconSize = 'medium'
): IconSourceSize => {
  const sizeMask = iconSizeMasks[name]
  const availableSizes = ICON_SOURCE_SIZES.filter(
    (_, index) => sizeMask & (1 << index)
  )
  const requestedSize = getRequestedSourceSize(size)

  return availableSizes.reduce((closest, candidate) => {
    const candidateDistance = Math.abs(candidate - requestedSize)
    const closestDistance = Math.abs(closest - requestedSize)

    return candidateDistance < closestDistance ||
      (candidateDistance === closestDistance && candidate > closest)
      ? candidate
      : closest
  })
}

const MissingIcon: FunctionComponent<{
  children?: React.ReactNode
  name: string
}> = ({ children, name }) => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    data-icon-missing={name}
  >
    {children}
    <rect
      x='4'
      y='4'
      width='16'
      height='16'
      rx='2'
      stroke='currentColor'
      strokeWidth='2'
      strokeDasharray='3 3'
    />
  </svg>
)

const PuxIcon: FunctionComponent<PuxIconProps> = ({
  name,
  size = 'medium',
  color = 'current',
  hoverColor,
  title,
  decorative = false,
  className,
}) => {
  const knownName = isKnownIconName(name) ? name : undefined
  const sourceSize = knownName
    ? resolveIconSourceSize(knownName, size)
    : undefined
  const spriteFile = sourceSize
    ? iconSpriteFiles[sourceSize][getIconSpriteBucket(name)]
    : undefined
  const symbolName = name.replace(/\//g, '--')
  const symbolHref =
    sourceSize && spriteFile
      ? `${withPrefix(`/${spriteFile}`)}#${symbolName}-size${sourceSize}`
      : undefined
  const classes = ['PuxIcon']

  if (className) {
    classes.push(className)
  }

  const isDecorative = decorative || !title
  const style: IconStyle = {
    '--PuxIcon-size': resolveIconSize(size),
    '--PuxIcon-color': resolveIconColor(color),
    '--PuxIcon-hover-color': resolveIconColor(hoverColor ?? color),
  }

  return (
    <span
      className={classes.join(' ')}
      style={style}
      aria-hidden={isDecorative}
      role={isDecorative ? undefined : 'img'}
      aria-label={isDecorative ? undefined : title}
    >
      {symbolHref && sourceSize ? (
        <svg focusable='false' data-icon-source-size={sourceSize}>
          {!isDecorative && title ? <title>{title}</title> : null}
          <use href={symbolHref} xlinkHref={symbolHref} />
        </svg>
      ) : (
        <MissingIcon name={name}>
          {!isDecorative && title ? <title>{title}</title> : null}
        </MissingIcon>
      )}
    </span>
  )
}

export default PuxIcon
export type { IconColor, IconName, IconSize, IconSourceSize, PuxIconProps }
